/**
 * E06-F02-T11 — Research navigation model: the pure (DOM-free) half of the
 * Research host's "real back" navigation.
 *
 * The bug: `ResearchSection` switched screens with a bare `setScreen(...)`, so
 *   - the browser/OS back gesture left Research altogether (no history entries),
 *   - the Library/History browser unmounted on every screen switch and lost the
 *     History panel + its search/status/page — returning always showed the
 *     Library panel with an empty search, so the recruiter had to re-open History
 *     and re-search the request every single time.
 *
 * The fix keeps the host's screen machine but mirrors every screen switch into
 * `window.history` (one entry per screen, same URL `/research`) and keeps the
 * browser state ({@link ResearchBrowseState}) in the host, above the unmounting
 * list. This module owns the entry shape and its (de)serialization so the
 * contract is unit-tested without a DOM (`researchNavModel.test.ts`).
 *
 * Every entry keeps `view: 'research'` at the top level, so the shell's own
 * `popstate` handler (`router/useRouter.ts` → `stateToRoute`) still resolves it
 * to the Research view and does not navigate away.
 */
import type { IntakeCriteria, ResearchRequestStatus, ResearchScreen } from '@eer/shared';
import { RESEARCH_REQUEST_STATUSES } from '@eer/shared';

/**
 * The host's screen set: the shared flow screens (`intake|run|review`) plus the
 * host-local ones — `library` (the entry point / browser), `aichain` (the manual
 * paid AI-check side-branch off Run, E06-F05-T05) and `publish` (the terminal
 * export step).
 */
export type HostScreen = ResearchScreen | 'library' | 'aichain' | 'publish';

export const HOST_SCREENS: readonly HostScreen[] = [
  'library',
  'intake',
  'run',
  'aichain',
  'review',
  'publish',
];

/** The single carried datum across screens (id + status + title + clone criteria). */
export interface FlowContext {
  readonly requestId: number;
  readonly status?: ResearchRequestStatus;
  /** The request title, when known (kept for the Run header). */
  readonly title?: string;
  /** For a History clone / Run "Edit criteria": the criteria to pre-fill Intake. */
  readonly cloneCriteria?: IntakeCriteria;
  /**
   * E12-F17-T04 — the request a prefill was COPIED from (History Clone / Run "Edit
   * criteria"). The copy is read-only (no row is persisted), so `requestId` stays 0
   * for such an Intake and Run / Save draft creates exactly one new request; this id
   * only keeps the source out of «Similar lists» and keys the form remount.
   */
  readonly sourceRequestId?: number;
  /**
   * E06-F02-T12 — the project the Intake form was linked to, kept with an Intake
   * entry so coming "Back" to the form restores the link as well as the criteria.
   */
  readonly projectId?: number;
}

export const EMPTY_CTX: FlowContext = { requestId: 0 };

/** The two Library/History entry points (prototype `state.panel`). */
export type ResearchPanel = 'library' | 'history';

/**
 * What the recruiter had set up in the browser: which panel was open and the
 * History search / status filter / page. Lives in the host so it survives the
 * browser unmounting while a flow screen (Run / Review / …) is shown.
 */
export interface ResearchBrowseState {
  readonly panel: ResearchPanel;
  readonly historyQuery: string;
  readonly historyStatus: 'all' | ResearchRequestStatus;
  readonly historyPage: number;
}

/**
 * E06-F04-T08 — entering Company Research opens History (the recruiter's own
 * requests); Library (raw material from every search) is the last subnav item.
 */
export const DEFAULT_BROWSE_STATE: ResearchBrowseState = {
  panel: 'history',
  historyQuery: '',
  historyStatus: 'all',
  historyPage: 1,
};

/** One Research history entry: the screen, its context and the browser state. */
export interface ResearchNavEntry {
  readonly screen: HostScreen;
  readonly ctx: FlowContext;
  readonly browse: ResearchBrowseState;
  /**
   * E06-F02-T12 — how many Research entries the host pushed BEFORE this one (0 = the
   * first Research screen of the visit). The "Back" button walks the browser history
   * only while it is > 0; at 0 the previous browser entry is not a Research screen,
   * so "Back" falls back to the screen's logical parent ({@link fallbackBackScreen}).
   */
  readonly depth?: number;
}

/** The `history.state` payload written for a Research entry. */
export interface ResearchHistoryState {
  readonly view: 'research';
  readonly research: ResearchNavEntry;
}

/** A restored entry. `browse` is absent when the entry carried none (keep the current one). */
export interface RestoredResearchEntry {
  readonly screen: HostScreen;
  readonly ctx: FlowContext;
  readonly browse?: ResearchBrowseState;
  /** E06-F02-T12 — the entry's depth; absent = 0 (see {@link ResearchNavEntry.depth}). */
  readonly depth?: number;
}

/** Build the `history.state` payload for an entry. */
export function toHistoryState(entry: ResearchNavEntry): ResearchHistoryState {
  return { view: 'research', research: entry };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asHostScreen(value: unknown): HostScreen | undefined {
  return (HOST_SCREENS as readonly unknown[]).includes(value) ? (value as HostScreen) : undefined;
}

function asStatus(value: unknown): ResearchRequestStatus | undefined {
  return (RESEARCH_REQUEST_STATUSES as readonly unknown[]).includes(value)
    ? (value as ResearchRequestStatus)
    : undefined;
}

function parseCtx(value: unknown): FlowContext {
  if (!isObject(value)) return EMPTY_CTX;
  const requestId =
    typeof value.requestId === 'number' && Number.isFinite(value.requestId) ? value.requestId : 0;
  const status = asStatus(value.status);
  const title = typeof value.title === 'string' ? value.title : undefined;
  const cloneCriteria = isObject(value.cloneCriteria)
    ? (value.cloneCriteria as unknown as IntakeCriteria)
    : undefined;
  const projectId =
    typeof value.projectId === 'number' && Number.isInteger(value.projectId) && value.projectId > 0
      ? value.projectId
      : undefined;
  const sourceRequestId =
    typeof value.sourceRequestId === 'number' &&
    Number.isInteger(value.sourceRequestId) &&
    value.sourceRequestId > 0
      ? value.sourceRequestId
      : undefined;
  return {
    requestId,
    ...(status ? { status } : {}),
    ...(title !== undefined ? { title } : {}),
    ...(cloneCriteria ? { cloneCriteria } : {}),
    ...(projectId !== undefined ? { projectId } : {}),
    ...(sourceRequestId !== undefined ? { sourceRequestId } : {}),
  };
}

/** Sanitize a browse snapshot read back from `history.state` (never trust it blindly). */
export function parseBrowseState(value: unknown): ResearchBrowseState | undefined {
  if (!isObject(value)) return undefined;
  const panel: ResearchPanel = value.panel === 'library' ? 'library' : DEFAULT_BROWSE_STATE.panel;
  const historyQuery = typeof value.historyQuery === 'string' ? value.historyQuery : '';
  const historyStatus = asStatus(value.historyStatus) ?? 'all';
  const rawPage = value.historyPage;
  const historyPage =
    typeof rawPage === 'number' && Number.isInteger(rawPage) && rawPage >= 1 ? rawPage : 1;
  return { panel, historyQuery, historyStatus, historyPage };
}

/**
 * Re-hydrate a Research entry from a `popstate` `history.state`.
 *   - not a Research entry (another view / no state) → `null`: the shell handles it;
 *   - the shell's own `{ view: 'research' }` entry (the one pushed by the sidebar
 *     nav, before any Research screen switch) → the browser (`library`), keeping
 *     the current browse state;
 *   - a Research entry → its screen / ctx / browse, sanitized.
 */
export function fromHistoryState(state: unknown): RestoredResearchEntry | null {
  if (!isObject(state) || state.view !== 'research') return null;
  const research = state.research;
  if (!isObject(research)) return { screen: 'library', ctx: EMPTY_CTX };
  const screen = asHostScreen(research.screen) ?? 'library';
  const ctx = screen === 'library' ? EMPTY_CTX : parseCtx(research.ctx);
  const browse = parseBrowseState(research.browse);
  const rawDepth = research.depth;
  const depth =
    typeof rawDepth === 'number' && Number.isInteger(rawDepth) && rawDepth > 0 ? rawDepth : 0;
  return {
    screen,
    ctx,
    ...(browse ? { browse } : {}),
    ...(depth > 0 ? { depth } : {}),
  };
}

/**
 * E06-F02-T12 — where "Back" lands when there is no earlier Research screen in the
 * browser history (the screen was the first one of the visit, e.g. a History row
 * opened straight onto Review): the screen's logical parent in the flow.
 *   publish → review, review → run, aichain → run, run|intake|library → library.
 */
export function fallbackBackScreen(screen: HostScreen): HostScreen {
  switch (screen) {
    case 'publish':
      return 'review';
    case 'review':
    case 'aichain':
      return 'run';
    default:
      return 'library';
  }
}

/** E06-F02-T12 — what the Intake form currently holds (reported up to the host). */
export interface IntakeDraftSnapshot {
  readonly criteria: IntakeCriteria;
  readonly title: string;
  readonly projectId: number | null;
}

/**
 * E06-F02-T12 — fold the Intake form's current values into the Intake entry's ctx,
 * so leaving the form (Run research, "Back", "Back to History") and coming back to
 * that entry re-opens the form with the criteria, title and project link the
 * recruiter had entered — nothing typed is lost.
 */
export function withIntakeDraft(ctx: FlowContext, draft: IntakeDraftSnapshot): FlowContext {
  return {
    requestId: ctx.requestId,
    ...(ctx.status ? { status: ctx.status } : {}),
    title: draft.title,
    cloneCriteria: draft.criteria,
    ...(draft.projectId !== null ? { projectId: draft.projectId } : {}),
    ...(ctx.sourceRequestId ? { sourceRequestId: ctx.sourceRequestId } : {}),
  };
}

/** Caption of the "back to the list" affordance — names the panel it returns to. */
export function backToListLabel(panel: ResearchPanel): string {
  return panel === 'history' ? 'Back to History' : 'Back to Library';
}

/** Structural equality of two browse snapshots (avoids no-op state updates). */
export function sameBrowseState(a: ResearchBrowseState, b: ResearchBrowseState): boolean {
  return (
    a.panel === b.panel &&
    a.historyQuery === b.historyQuery &&
    a.historyStatus === b.historyStatus &&
    a.historyPage === b.historyPage
  );
}
